import { Button, Divider, Flex, message } from "antd";
import { TopBar } from "../../../../widgets";
import { useEffect, useRef, useState } from "react";
import { isLogin } from "../../../../utils";
import { getMemberDataByToken, removeUrl, removeUser, setUrl } from "../../../../service";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { loadPaymentWidget, PaymentWidgetInstance } from "@tosspayments/payment-widget-sdk";
import { useGetProgramBookingWebByProgramBookingId } from "../../../my/reservationHistory/detail/programBookingDetail/model";
import dayjs, { Dayjs } from "dayjs";
import { DividerThin } from "../../../../shared";
import './index.css'

const ProgramBookingCheckout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [memberData, setMemberData] = useState<IMember | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const paymentWidgetRef = useRef<PaymentWidgetInstance | null>(null)
    const paymentMethodsWidgetRef = useRef<ReturnType<PaymentWidgetInstance["renderPaymentMethods"]> | null>(null)
    const agreementWidgetRef = useRef<any | null>(null)
    const [isAgreement, setIsAgreement] = useState<boolean>(false)
    const programBookingId = Number(useParams().programBookingId)
    const clientKey = `${process.env.REACT_APP_TOS_PAY_CLIENT_KEY}`
    const { data, isFetching } = useGetProgramBookingWebByProgramBookingId(programBookingId, 'programId programBookingId programIsOnline programThumbnailImageUrl programTitle programDescription programLocationName memberId memberName memberMobile targetDateTime programIsPaid programBookingIsPaid programBookingStatus programRefundNotiHtml totalPayAmount paymentDeadline programBookingSelectOptionList { programSelectOptionName programSelectOptionItemNameList } programBookingSubjectOptionList { programSubjectOptionName content } programBookingTicketWebResponseList { programBookingTicketCount programTicketName programTicketPayAmount programBookingTicketPrice }')

    const requesttossPayment = async () => {
        try {
            if (!memberData) return;
            const orderId = `program${data?.programBookingId}-${dayjs().format('YYYYMMDDHHmmssZ').replaceAll('+', '').replaceAll(':', '')}` + `-${memberData.mobile}`
            const orderName = `${data?.programTitle} ${dayjs(data?.targetDateTime).format("YYYYMMDDHHmmss")}`
            const paymentWidget = paymentWidgetRef.current
            await paymentWidget?.requestPayment({
                orderId: orderId,
                orderName: orderName,
                customerName: memberData.name,
                customerEmail: memberData.email,
                customerMobilePhone: memberData.mobile,
                successUrl: `${window.location.origin}/program-booking-toss-pay/comfirm?programBookingId=${programBookingId}`,
                failUrl: `${window.location.origin}/fail`,
            })
        } catch (error: any) { console.error("Error: ", error); message.error(error?.message ?? "오류가 발생했습니다. 다시 시도해 주세요."); }
    }

    const checkPaymentDeadlineIsToday = (paymentDeadline: Dayjs) => {
        const today = dayjs().format('YYYY.MM.DD')
        const tomorrow = dayjs().add(1, 'day').format('YYYY.MM.DD')
        const targetDate = paymentDeadline.format('YYYY.MM.DD')
        if (today === targetDate) {
            return '오늘 ' + paymentDeadline.format('HH:mm')
        } else if (tomorrow === targetDate) {
            return '내일 ' + paymentDeadline.format('HH:mm')
        } else {
            return paymentDeadline.format('YYYY.MM.DD HH:mm')
        }
    }

    useEffect(() => {
        if (!data) return;
        const paymentDeadline = dayjs(data.paymentDeadline)
        if (paymentDeadline.isBefore(dayjs())) {
            return navigate('/program-booking/expire')
        }
    }, [data])

    useEffect(() => {
        if (!memberData || !data) return;
        setLoading(true)
        loadPaymentWidget(clientKey, memberData.mobile)
            .then(async (paymentWidget) => {
                paymentMethodsWidgetRef.current = paymentWidget.renderPaymentMethods(
                    "#payment-widget",
                    { value: data?.totalPayAmount ?? 0 },
                    { variantKey: "PROGRAM" }
                );
                paymentMethodsWidgetRef.current.updateAmount(data.totalPayAmount)
                try {
                    agreementWidgetRef.current = paymentWidget.renderAgreement("#agreement", {
                        variantKey: "AGREEMENT",
                    })
                    agreementWidgetRef.current.on("change", (e: any) => { setIsAgreement(e?.agreedRequiredTerms) })

                } catch (e) {
                    console.error("Agreement 위젯 로딩 실패:", e);
                }
                paymentWidgetRef.current = paymentWidget;

                setLoading(false);
            })
            .catch((error) => {
                console.error("결제 위젯 로딩 에러:", error);
            });
    }, [memberData, data])

    useEffect(() => {
        if (!isLogin()) {
            setUrl(location.pathname);
            navigate("/login");
            return;
        }

        getMemberDataByToken()
            .then(res => {
                setMemberData(res.data);
                removeUrl()
            })
            .catch(error => {
                console.error('error', error);
                setUrl(location.pathname);
                removeUser();
                navigate("/login");
            })
    }, [])

    return <>
        <TopBar title="결제" rightIcon={false} />
        <div style={{ padding: '24px 20px 32px' }}>
            <div className="sub-title/st5 secondary/s800">
                예약 정보
            </div>
            <div style={{ marginTop: 20, padding: 12, backgroundColor: 'var(--secondarys100)', borderRadius: 4 }} onClick={() => navigate(`/wellness-program/${data?.programId}`)}>
                <Flex justify="space-between" align='center' gap={16}>
                    <Flex align='center' gap={16}>
                        <div style={{ width: 56, height: 56, minWidth: 56 }}>
                            <img src={data?.programThumbnailImageUrl} alt="프로그램 이미지" width={'100%'} height={'100%'} style={{ borderRadius: 2, objectFit: 'cover' }} />
                        </div>
                        <div>
                            <div className="sub-title/st3">{data?.programTitle}</div>
                            <div className="body/b1 secondary/s500" style={{ marginTop: 4 }}>{data?.programIsOnline ? '온라인 프로그램' : data?.programLocationName}</div>
                        </div>
                    </Flex>
                </Flex>
            </div>
            <div className="body/b2 error/e700" style={{ textAlign: 'center', marginBlock: 20 }}>{checkPaymentDeadlineIsToday(dayjs(data?.paymentDeadline))} 까지 미결제 시 예약 자동 취소</div>
            <div>
                <Flex justify='space-between' align='center'>
                    <div className="body/b2 secondary/s600">예약 일시</div>
                    <div className="body/b2">{dayjs(data?.targetDateTime).format("YYYY.MM.DD (ddd) HH:mm")}</div>
                </Flex>
                <Divider style={{ marginBlock: 16 }} />
                {data?.programBookingTicketWebResponseList
                    .filter(bookingTicket => bookingTicket.programBookingTicketCount > 0)
                    .map((bookingTicket, idx, arr) =>
                        <Flex key={idx} justify='space-between' align='center' style={{ marginBottom: (idx === arr.length - 1) ? '' : 8 }}>
                            <div className="secondary/s800" style={{ marginBottom: idx !== arr.length - 1 ? 8 : 0 }}>
                                <div className="sub-title/st2">{bookingTicket.programTicketName}</div>
                                <div className="body/b2" style={{ marginTop: 4 }}>{bookingTicket.programBookingTicketPrice.toLocaleString()}원</div>
                            </div>
                            <div className="body/b2">{bookingTicket.programBookingTicketCount}명</div>
                        </Flex>)}
                {(data?.programBookingSelectOptionList.length !== 0 || data?.programBookingSubjectOptionList.length !== 0) && <Divider style={{ marginBlock: 16 }} />}
                {data?.programBookingSelectOptionList.map((selectOption, idx) =>
                    <div key={idx}>
                        <Flex className="body/b2" justify='space-between' align='center'>
                            <div className="secondary/s600">{selectOption.programSelectOptionName}</div>
                            <div>{selectOption.programSelectOptionItemNameList.join(', ')}</div>
                        </Flex>
                        {data?.programBookingSubjectOptionList.length !== 0 && <Divider style={{ marginBlock: 16 }} />}
                    </div>)}
                {data?.programBookingSubjectOptionList.map((subjectOption, idx, arr) =>
                    <div key={idx}>
                        <Flex className="body/b2" justify='space-between' align='center'>
                            <div className="secondary/s600">{subjectOption.programSubjectOptionName}</div>
                            <div>{subjectOption.content}</div>
                        </Flex>
                        {idx !== (arr.length - 1) && <Divider style={{ marginBlock: 16 }} />}
                    </div>)}
            </div>
        </div>
        <DividerThin style={{ height: 6, marginBottom: 32 }} />
        <div style={{ paddingInline: 20, marginBottom: 32 }}>
            <div className="sub-title/st5 secondary/s800" style={{ paddingBottom: 24 }}>결제 정보</div>
            <Flex className="body/b2" justify='space-between'>
                <div className="secondary/s600">
                    금액
                </div>
                <div>
                    {data?.totalPayAmount?.toLocaleString()}원
                </div>
            </Flex>
            <Divider style={{ backgroundColor: 'var(--secondarys500)', marginBlock: 16 }} />
            <Flex justify='space-between'>
                <div className="sub-title/st2 secondary/s850">
                    총 결제금액
                </div>
                <div className="sub-title/st3 secondary/s850">
                    {data?.totalPayAmount?.toLocaleString()}원
                </div>
            </Flex>
        </div>
        <DividerThin style={{ height: 6, marginBottom: 32 }} />
        <div className="sub-title/st5 secondary/s800" style={{ paddingInline: 20 }}>
            결제 방법
        </div>
        <div id="payment-widget" />

        <div style={{ marginInline: 20, marginTop: 12, padding: 12, borderRadius: 'var(--radius4)', backgroundColor: 'var(--secondarys100)' }}>
            <div className="sub-title/st2 base/black" style={{ marginBottom: 12 }}>
                취소 ・ 환불 규정
            </div>
            <div className="body/b2 secondary/s800 wellness-program-booking-checkout-container" dangerouslySetInnerHTML={{ __html: data?.programRefundNotiHtml ?? '' }}>

            </div>
        </div>

        <div id="agreement" />

        <div style={{ padding: '0 20px 40px' }}>
            <Button
                disabled={!isAgreement}
                loading={loading || isFetching}
                className="sub-title/st3 base/white"
                type="primary"
                style={{ width: '100%', height: '50px', border: 'none' }}
                onClick={() => requesttossPayment()}
            >
                {Number(data?.totalPayAmount ?? 0).toLocaleString()}원 결제하기
            </Button>
        </div>
    </>
}

export default ProgramBookingCheckout;